import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { H_HYDRO_OPTIONS } from '../../../account/constants/AccessRulesConstants'
import StationAction from '../../../station/actions/StationAction'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { getSetting, getSettingBool, getSettingInt } from '../../../utils/SettingUtils'
import AdministrationAction from '../../actions/AdministrationAction'
import DataTypesTable from '../dataTypes/DataTypesTable'
import ShowAssociatedDataRuleTable from '../dataTypes/ShowAssociatedDataRuleTable'
import Select from '../../../components/forms/Select'
import ContributorAction from '../../../referencial/components/contributor/actions/ContributorAction'
import { getLabel } from '../../../utils/StoreUtils'
import FrequencyStatCalculationMethodPopup from '../piezometer/FrequencyStatCalculationMethodPopup'
import Checkbox from '../../../components/forms/Checkbox'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import { Button, Card, CardContent, Grid, Stack, Typography } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import { darkBlue } from 'utils/constants/ColorTheme'
import { Edit } from '@mui/icons-material'
import { omitBy, isObject } from 'lodash'

const project = 'SIH'

const AdminHydrometricStationApp = () => {
    const {
        applicationSettingsStore,
        contributors,
        filteredDataTypes,
    } = useSelector(store => ({
        applicationSettingsStore: store.AdministrationReducer.applicationSettings,
        contributors: store.ContributorReducer.contributors,
        filteredDataTypes: store.StationReducer.filteredDataTypes,
    }), shallowEqual)

    const [readMode, setReadMode] = useState(true)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [applicationSettings, setApplicationSettings] = useState(applicationSettingsStore)
    const [frequencyCalculationPopupIsOpen, setFrequencyCalculationPopupIsOpen] = useState(false)
    const [dataTypes, setDataTypesState] = useState([])

    const dispatch = useDispatch()

    const setDataTypes = () => {
        dispatch(StationAction.fetchDataTypesByProject(project)).then((res) => {
            setDataLoaded(true)
            setDataTypesState(res)
        })
    }

    useEffect(() => {
        if (!componentHasHabilitations(H_HYDRO_OPTIONS)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return
        }
        dispatch(ContributorAction.fetchContributors())
        setDataTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onCancel = () => {
        setDataTypes(filteredDataTypes)
        setApplicationSettings(applicationSettingsStore)
        setReadMode(true)
    }

    const onSave = () => {
        setDataLoaded(false)
        dispatch(StationAction.updateDataTypesByProject(project, dataTypes.map(d => omitBy(d, isObject)))).then(() => {
            setDataTypes()
        })
        const applicationSettingsFiltered = applicationSettings.filter(e => e.value !== getSetting(applicationSettingsStore, e.parameter))
        if (applicationSettingsFiltered.length) {
            dispatch(AdministrationAction.updateSieauParameters(applicationSettingsFiltered))
        }
        setReadMode(true)
    }

    useActions(() => {
        if (readMode) {
            return {
                edit: () => setReadMode(false),
            }
        }
        return {
            save: () => onSave(),
            cancel: () => onCancel(),
        }
    }, [readMode, applicationSettings, applicationSettingsStore, dataTypes])

    useTitle(() => [{
        title: i18n.hydrometry,
        href: 'hydrometry',
    }, {
        title: i18n.hydrometryOptions,
        href: 'hydrometry/hydrometryOptions',
    }], [])

    const onChangeSetting = (setting, value) => {
        const stateObj = applicationSettings.filter(e => e.parameter !== setting)
        setApplicationSettings([...stateObj, { parameter: setting, value: value ? value.toString() : value }])
    }

    const frequencyCalculationMethods = [
        { code: 'normal', label: 'Loi normale' },
        { code: 'trunc', label: 'Loi normale tronquée' },
        { code: 'percent', label: 'Pourcentage par rapport à la moyenne' },
        { code: 'noLaw', label: 'Sans loi statistique' },
    ]

    return (
        <Stack spacing={2} padding={1}>
            <Card>
                <CardTitle title={i18n.hydrometryOptions} color={darkBlue} />
                <CardContent>
                    <Stack spacing={1}>
                        <Typography variant={'h5'}>{i18n.returnPeriodCalculationMethod}</Typography>
                        <Typography>{i18n.returnPeriodCalculationMethodExplaination}</Typography>
                        <Grid container>
                            <Grid size={3}>
                                <Button variant='contained' startIcon={<Edit />} onClick={() => setFrequencyCalculationPopupIsOpen(true)} disabled={readMode}>{i18n.changeCalculationMethod}</Button>
                            </Grid>
                            <Grid size={9}>
                                <Typography variant={'h6'}>{i18n.currentMethod}
                                : { getLabel(frequencyCalculationMethods, getSetting(applicationSettings, 'FrequencyStatMode') ?? i18n.normal) }</Typography>
                            </Grid>
                        </Grid>
                        <Checkbox
                            label={i18n.showAllReturnPeriodsWithoutHistoCondition}
                            disabled={readMode}
                            onChange={ v => onChangeSetting('showAllReturnPeriodsHydroWithoutHistoCondition', v.toString()) }
                            checked={ getSettingBool(applicationSettings, 'showAllReturnPeriodsHydroWithoutHistoCondition') ?? false }
                        />
                        <Checkbox
                            label={i18n.enableHydroportailExports}
                            disabled={readMode}
                            onChange={ v => onChangeSetting('HYDROPORTAILisActive', v.toString()) }
                            checked={ getSettingBool(applicationSettings, 'HYDROPORTAILisActive') ?? true }
                        />
                        <Typography variant={'h5'}>{i18n.validationScreenDefaultValues}</Typography>
                        <Grid container spacing={1}>
                            <Grid size={4}>
                                <Select
                                    disabled={readMode}
                                    label={i18n.producer}
                                    options={contributors}
                                    value={getSettingInt(applicationSettings, 'validationDefaultProducerHydro')}
                                    onChange={v => onChangeSetting('validationDefaultProducerHydro', v)}
                                    keyValue='id'
                                    keyLabel='mnemonique'
                                />
                            </Grid>
                            <Grid size={4}>
                                <Select
                                    disabled={readMode}
                                    label={i18n.administrator}
                                    options={contributors}
                                    value={getSettingInt(applicationSettings, 'validationDefaultManagerHydro')}
                                    onChange={v => onChangeSetting('validationDefaultManagerHydro', v)}
                                    keyValue='id'
                                    keyLabel='mnemonique'
                                />
                            </Grid>
                            <Grid size={4}>
                                <Select
                                    disabled={readMode}
                                    label={i18n.validator}
                                    options={contributors}
                                    value={getSetting(applicationSettings, 'validationDefaultValidatorHydro')}
                                    onChange={v => onChangeSetting('validationDefaultValidatorHydro', v)}
                                    keyValue='id'
                                    keyLabel='mnemonique'
                                />
                            </Grid>
                        </Grid>
                    </Stack>
                </CardContent>
            </Card>
            {dataLoaded && (
                <DataTypesTable
                    project={project}
                    dataTypes={dataTypes}
                    readMode={readMode}
                    onChange={(data) => setDataTypesState(data)}
                />
            )}
            {dataLoaded && (
                <ShowAssociatedDataRuleTable
                    currentSettings={applicationSettings}
                    fromStationType='HYDRO'
                    toStationType='PIEZO'
                    onChangeRules={ rules => onChangeSetting('showDataTypesRules', rules) }
                    readMode={readMode}
                />
            )}
            {dataLoaded && (
                <ShowAssociatedDataRuleTable
                    currentSettings={applicationSettings}
                    fromStationType='HYDRO'
                    toStationType='PLUVIO'
                    onChangeRules={ rules => onChangeSetting('showDataTypesRules', rules) }
                    readMode={readMode}
                />
            )}
            <FrequencyStatCalculationMethodPopup
                selectedMode={getSetting(applicationSettings, 'FrequencyStatMode') ?? 'normal'}
                changeMode={mode => onChangeSetting('FrequencyStatMode', mode)}
                onClose={() => setFrequencyCalculationPopupIsOpen(false)}
                isOpen={frequencyCalculationPopupIsOpen}
            />
        </Stack>
    )
}

export default AdminHydrometricStationApp